import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { fetchWithAuth } from "../FetchWithAuth";

/** Sidebar stack: solid fills (readable on dark nav). */
const STACK_AVATARS = [
  { bg: "#3949ab", fg: "#ffffff", border: "#283593" },
  { bg: "#00796b", fg: "#ffffff", border: "#004d40" },
  { bg: "#6d4c41", fg: "#ffffff", border: "#4e342e" },
];

/** Hover panel avatars. */
const TOOLTIP_AVATARS = [
  { bg: "#e8eaf6", fg: "#1a237e", border: "#7986cb" },
  { bg: "#e0f2f1", fg: "#004d40", border: "#4db6ac" },
  { bg: "#efebe9", fg: "#3e2723", border: "#8d6e63" },
];

const MORE_STACK = { bg: "#37474f", fg: "#eceff1", border: "#263238" };

const TOOLTIP_PANEL_SX = {
  bgcolor: "#ffffff",
  color: "#1a1a1a",
  maxWidth: 340,
  p: "12px",
  borderRadius: "8px",
  pointerEvents: "auto",
  border: "1px solid #d1d5db",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
};

function str(v) {
  if (v == null) return "";
  return String(v).trim();
}

function initials(user) {
  const f = str(user?.first_name);
  const l = str(user?.last_name);
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  const u = str(user?.username) || "?";
  return u.slice(0, 2).toUpperCase();
}

function displayName(user) {
  const f = str(user?.first_name);
  const l = str(user?.last_name);
  if (f || l) return `${f} ${l}`.trim();
  return str(user?.username) || "User";
}

function formatIdle(idleSeconds, isIdle) {
  if (idleSeconds == null) return "Activity unknown";
  if (!isIdle || idleSeconds < 60) return "Active now";
  const total = Math.floor(idleSeconds);
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return mins > 0 ? `Idle ${hours}h ${mins}m` : `Idle ${hours}h`;
  }
  return `Idle ${mins}m`;
}

function presenceSecondary(user) {
  const idleLine = formatIdle(user?.idle_seconds, user?.is_idle);
  const page =
    str(user?.current_page) ||
    (user?.enquiry_id
      ? `Enquiry: ${str(user.enquiry_name) || "Unknown"} (#${user.enquiry_id})`
      : "");
  if (page) return `${idleLine} · ${page}`;
  return idleLine;
}

const AVATAR_PX = 30;
const OVERLAP_PX = 10;

export default function SidebarOnlineStaff({
  apiUrl,
  currentUsername,
  themeMode = "default",
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const ringColor =
    themeMode === "light" || themeMode === "default" ? "#e2e8f0" : "#0f172a";

  const load = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/dashboard/online-users`);
      if (!response.ok) {
        setUsers([]);
        return;
      }
      const data = await response.json();
      const raw = Array.isArray(data?.users)
        ? data.users
        : Array.isArray(data)
          ? data
          : [];
      const normalized = raw
        .map((u) =>
          typeof u === "string"
            ? { username: u, first_name: "", last_name: "" }
            : u
        )
        .filter(Boolean);
      setUsers(normalized);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 45_000);
    return () => window.clearInterval(t);
  }, [load]);

  const others = useMemo(() => {
    const self = str(currentUsername).toLowerCase();
    const list = users.filter(
      (u) => str(u.username).toLowerCase() !== self
    );
    list.sort((a, b) =>
      displayName(a).localeCompare(displayName(b), undefined, {
        sensitivity: "base",
      })
    );
    return list;
  }, [users, currentUsername]);

  const top3 = others.slice(0, 3);
  const surplus = Math.max(0, others.length - 3);
  const stackCount = top3.length + (surplus > 0 ? 1 : 0);

  const tooltipTitle =
    others.length === 0 ? (
      <Typography
        variant="caption"
        sx={{ color: "#4b5563", display: "block", fontSize: "0.8125rem" }}
      >
        No other staff online right now.
      </Typography>
    ) : (
      <List
        dense
        disablePadding
        sx={{ maxHeight: 320, overflow: "auto", py: 0, bgcolor: "#ffffff" }}
      >
        {others.map((u, idx) => {
          const tip = TOOLTIP_AVATARS[idx % TOOLTIP_AVATARS.length];
          return (
            <ListItem
              key={`${str(u.username)}-${u.id ?? idx}`}
              disableGutters
              alignItems="flex-start"
              sx={{
                py: 0.75,
                px: 0.25,
                borderBottom:
                  idx < others.length - 1 ? "1px solid #e5e7eb" : "none",
              }}
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    border: `2px solid ${tip.border}`,
                    bgcolor: tip.bg,
                    color: tip.fg,
                  }}
                >
                  {initials(u)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={displayName(u)}
                secondary={
                  <>
                    {str(u.username) ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "#6b7280", display: "block", fontSize: "0.75rem" }}
                      >
                        @{str(u.username)}
                      </Typography>
                    ) : null}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: "#374151",
                        display: "block",
                        fontSize: "0.72rem",
                        lineHeight: 1.35,
                        mt: 0.15,
                      }}
                    >
                      {presenceSecondary(u)}
                    </Typography>
                  </>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  sx: {
                    color: "#111827",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  },
                }}
                secondaryTypographyProps={{
                  component: "div",
                }}
              />
            </ListItem>
          );
        })}
      </List>
    );

  const stackRing = { boxShadow: `0 0 0 2px ${ringColor}` };

  if (loading) {
    return (
      <Box
        aria-hidden
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
          pl: 0.25,
          minHeight: AVATAR_PX,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="circular"
            width={AVATAR_PX}
            height={AVATAR_PX}
            sx={{
              ml: i === 0 ? 0 : `-${OVERLAP_PX}px`,
              zIndex: i + 1,
              bgcolor: STACK_AVATARS[i % STACK_AVATARS.length].bg,
              ...stackRing,
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Tooltip
      title={tooltipTitle}
      placement="right"
      enterDelay={200}
      disableInteractive={false}
      slotProps={{
        tooltip: { sx: TOOLTIP_PANEL_SX },
      }}
    >
      <Box
        role="group"
        aria-label="Other staff currently online"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
          minHeight: AVATAR_PX + 4,
        }}
      >
        {stackCount === 0 ? (
          <Avatar
            sx={{
              width: AVATAR_PX,
              height: AVATAR_PX,
              border: `2px solid ${MORE_STACK.border}`,
              bgcolor: MORE_STACK.bg,
              color: MORE_STACK.fg,
              ...stackRing,
            }}
            title="No other staff online"
          >
            <MoreHorizIcon sx={{ fontSize: 16 }} />
          </Avatar>
        ) : (
          <Box
            sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}
          >
            {top3.map((u, i) => {
              const pal = STACK_AVATARS[i % STACK_AVATARS.length];
              return (
                <Avatar
                  key={str(u.username) || `p-${u.id}-${i}`}
                  title={`${displayName(u)} — ${presenceSecondary(u)}`}
                  sx={{
                    width: AVATAR_PX,
                    height: AVATAR_PX,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    border: `2px solid ${pal.border}`,
                    bgcolor: pal.bg,
                    color: pal.fg,
                    boxSizing: "border-box",
                    ...stackRing,
                    ml: i === 0 ? 0 : `-${OVERLAP_PX}px`,
                    zIndex: i + 1,
                  }}
                >
                  {initials(u)}
                </Avatar>
              );
            })}
            {surplus > 0 && (
              <Avatar
                sx={{
                  width: AVATAR_PX,
                  height: AVATAR_PX,
                  fontSize: surplus > 9 ? "0.6rem" : "0.68rem",
                  fontWeight: 700,
                  border: `2px solid ${MORE_STACK.border}`,
                  bgcolor: MORE_STACK.bg,
                  color: MORE_STACK.fg,
                  boxSizing: "border-box",
                  ...stackRing,
                  ml: top3.length ? `-${OVERLAP_PX}px` : 0,
                  zIndex: top3.length + 1,
                }}
              >
                +{surplus}
              </Avatar>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
